import React, { useEffect, useState } from 'react';
import { Plus, Search, X, Circle, Trash2, ChevronDown, Edit } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from '../../hooks/useNavigate';
import { ConfirmModal } from '../common/ConfirmModal';

interface Trailer {
  id: string;
  unit_number: string;
  vin: string;
  year: number;
  make: string;
  model: string;
  trailer_type: string;
  length_feet: number;
  width_feet?: number;
  height_feet?: number;
  weight_lbs?: number;
  license_plate: string;
  license_state: string;
  registration_expiration: string;
  inspection_expiration: string;
  capacity_weight: number;
  tire_size?: string;
  serial_number?: string;
  tag_number?: string;
  tag_state?: string;
  dot_issued_date?: string;
  dot_state?: string;
  dot_expiration_date?: string;
  insurance_type?: string;
  insurance_carrier?: string;
  insurance_expiration_date?: string;
  registration_file_url?: string;
  dot_file_url?: string;
  status: string;
  notes: string;
  gps: string;
  owner: string;
  driver_assigned: string;
  truck_id: string | null;
  trucks?: {
    id: string;
    unit_number: string;
    drivers?: {
      id: string;
      first_name: string;
      last_name: string;
    };
  };
}

export function TrailersView() {
  const toast = useToast();
  const navigate = useNavigate();
  const [trailers, setTrailers] = useState<Trailer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [trailerToDelete, setTrailerToDelete] = useState<Trailer | null>(null);
  const [selectedTrailer, setSelectedTrailer] = useState<Trailer | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [trailerForStatusChange, setTrailerForStatusChange] = useState<Trailer | null>(null);
  const [editingGPS, setEditingGPS] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedTrailerForNote, setSelectedTrailerForNote] = useState<Trailer | null>(null);
  const [noteInput, setNoteInput] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [collapsedStatuses, setCollapsedStatuses] = useState<Set<string>>(new Set([
    'ACTIVE',
    'ALERT',
    'UGOVOR TURE',
    'RECOVERY',
    'JARDA - ready for new drivers',
    'SHOP',
    'INACTIVE'
  ]));

  useEffect(() => {
    loadTrailers();
  }, []);

  const handleDeleteTrailer = async () => {
    if (!trailerToDelete) return;

    try {
      const { error } = await supabase
        .from('trailers')
        .delete()
        .eq('id', trailerToDelete.id);

      if (error) throw error;

      toast.success('Trailer deleted successfully!');
      setShowDeleteConfirm(false);
      setTrailerToDelete(null);
      loadTrailers();
    } catch (error: any) {
      console.error('Error deleting trailer:', error);
      
      // Check if it's a foreign key constraint error
      if (error.code === '23503') {
        toast.error('Cannot delete trailer: It is referenced by other records.');
      } else {
        toast.error('Failed to delete trailer');
      }
      
      setShowDeleteConfirm(false);
      setTrailerToDelete(null);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!trailerForStatusChange) return;

    try {
      const { error } = await supabase
        .from('trailers')
        .update({ status: newStatus })
        .eq('id', trailerForStatusChange.id);

      if (error) throw error;

      toast.success(`Trailer status changed to ${newStatus}`);
      setShowStatusModal(false);
      setTrailerForStatusChange(null);
      loadTrailers();
    } catch (error: any) {
      console.error('Error changing trailer status:', error);
      toast.error('Failed to change trailer status');
    }
  };

  const handleGPSChange = async (trailerId: string, newGPS: string) => {
    try {
      const { error } = await supabase
        .from('trailers')
        .update({ gps: newGPS })
        .eq('id', trailerId);

      if (error) throw error;

      toast.success('GPS updated successfully');
      setEditingGPS(null);
      loadTrailers();
    } catch (error: any) {
      console.error('Error updating GPS:', error);
      toast.error('Failed to update GPS');
    }
  };

  const handleSaveNote = async () => {
    if (!selectedTrailerForNote) return;
    
    setSavingNote(true);
    try {
      const { error } = await supabase
        .from('trailers')
        .update({ notes: noteInput || null })
        .eq('id', selectedTrailerForNote.id);
      
      if (error) throw error;
      
      toast.success('Note saved successfully!');
      setShowNoteModal(false);
      setSelectedTrailerForNote(null);
      setNoteInput('');
      loadTrailers();
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  async function loadTrailers() {
    try {
      // First get all trailers
      const { data: trailersData, error: trailersError } = await supabase
        .from('trailers')
        .select('*')
        .order('unit_number');

      if (trailersError) throw trailersError;

      // Get current equipment assignments to find drivers for trucks
      const { data: assignments } = await supabase
        .from('equipment_assignments')
        .select('truck_id, driver_id, drivers(id, first_name, last_name)')
        .eq('is_current', true);

      // Get trucks data with their trailer assignments
      const { data: trucksData } = await supabase
        .from('trucks')
        .select('id, unit_number, trailers');

      // Create a map of truck_id -> driver info
      const truckDriverMap = new Map();
      if (assignments && trucksData) {
        assignments.forEach((assignment: any) => {
          if (assignment.truck_id && assignment.drivers) {
            truckDriverMap.set(assignment.truck_id, {
              id: assignment.drivers.id,
              first_name: assignment.drivers.first_name,
              last_name: assignment.drivers.last_name
            });
          }
        });
      }

      // Create a map of truck_id -> truck info with driver
      const trucksMap = new Map();
      // Also create a map of trailer unit number -> truck info (for trailers field matching)
      const trailerUnitToTruckMap = new Map();
      
      if (trucksData) {
        trucksData.forEach(truck => {
          const truckInfo = {
            id: truck.id,
            unit_number: truck.unit_number,
            drivers: truckDriverMap.get(truck.id)
          };
          trucksMap.set(truck.id, truckInfo);
          
          // If truck has trailers assigned (array of trailer unit numbers), map each trailer to this truck
          if (truck.trailers) {
            const trailerUnits = Array.isArray(truck.trailers) 
              ? truck.trailers 
              : (typeof truck.trailers === 'string' ? truck.trailers.split(',').map((t: string) => t.trim()) : []);
            trailerUnits.forEach((trailerUnit: string) => {
              trailerUnitToTruckMap.set(trailerUnit, truckInfo);
            });
          }
        });
      }

      // Attach truck info to trailers
      const trailersWithTrucks = (trailersData || []).map(trailer => {
        // First check if trailer has truck_id (foreign key relationship)
        let truckInfo = trailer.truck_id ? trucksMap.get(trailer.truck_id) : undefined;
        
        // If no truck_id, check if this trailer's unit number is in any truck's trailers field
        if (!truckInfo) {
          truckInfo = trailerUnitToTruckMap.get(trailer.unit_number);
        }
        
        return {
          ...trailer,
          trucks: truckInfo
        };
      });

      console.log('Final trailers with trucks:', trailersWithTrucks);
      setTrailers(trailersWithTrucks);
    } catch (error) {
      console.error('Error loading trailers:', error);
    } finally {
      setLoading(false);
    }
  }

  const filteredTrailers = trailers.filter((trailer) =>
    trailer.unit_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trailer.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trailer.model?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get unique statuses from trailers and merge with predefined ones
  const allStatuses = Array.from(new Set([
    'ACTIVE',
    'ALERT',
    'UGOVOR TURE',
    'RECOVERY',
    'JARDA - ready for new drivers',
    'SHOP',
    'INACTIVE',
    ...trailers.map(t => t.status).filter(Boolean)
  ]));

  const getStatusConfig = (status: string) => {
    const predefined: Record<string, any> = {
      'ACTIVE': {
        label: 'ACTIVE',
        description: 'Active trailers in service',
        color: 'bg-green-100 text-green-700'
      },
      'ALERT': {
        label: 'ALERT',
        description: 'Alert - returning or changing',
        color: 'bg-red-100 text-red-700'
      },
      'ALERT / vracanje / promjena': {
        label: 'ALERT',
        description: 'Alert - returning or changing',
        color: 'bg-red-100 text-red-700'
      },
      'UGOVOR TURE': {
        label: 'UGOVOR TURE',
        description: 'Contract tours',
        color: 'bg-purple-100 text-purple-700'
      },
      'RECOVERY': {
        label: 'RECOVERY',
        description: 'Recovery mode',
        color: 'bg-orange-100 text-orange-700'
      },
      'JARDA - ready for new drivers': {
        label: 'JARDA - ready for new drivers',
        description: 'Ready for new drivers at yard',
        color: 'bg-cyan-100 text-cyan-700'
      },
      'SHOP': {
        label: 'SHOP',
        description: 'In shop for maintenance',
        color: 'bg-indigo-100 text-indigo-700'
      },
      'INACTIVE': {
        label: 'INACTIVE',
        description: 'Inactive trailers',
        color: 'bg-gray-100 text-gray-700'
      },
      'available': {
        label: 'AVAILABLE',
        description: 'Available trailers',
        color: 'bg-green-100 text-green-700'
      },
      'in_use': {
        label: 'IN USE',
        description: 'Trailers currently in use',
        color: 'bg-blue-100 text-blue-700'
      },
      'in_shop': {
        label: 'IN SHOP',
        description: 'Trailers in maintenance',
        color: 'bg-yellow-100 text-yellow-700'
      },
      'out_of_service': {
        label: 'OUT OF SERVICE',
        description: 'Out of service trailers',
        color: 'bg-red-100 text-red-700'
      }
    };

    return predefined[status] || {
      label: status?.toUpperCase() || 'UNKNOWN',
      description: status || 'Unknown status',
      color: 'bg-gray-100 text-gray-700'
    };
  };

  const toggleStatusCollapse = (status: string) => {
    setCollapsedStatuses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      return newSet;
    });
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Trailers <span className="text-slate-500">({trailers.length})</span>
          </h1>
          <p className="text-sm text-slate-600 mt-1">Manage your trailer fleet</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
        >
          <Plus className="w-5 h-5" />
          Add Trailer
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search trailers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <tbody>
              {allStatuses.map((status) => {
                const statusTrailers = filteredTrailers.filter(t => t.status === status);
                const isCollapsed = collapsedStatuses.has(status);
                
                return (
                  <React.Fragment key={status}>
                    <tr>
                      <td colSpan={12} className="p-0">
                        <div
                          className={`px-4 py-3 cursor-pointer flex items-center justify-between ${
                            status === 'ACTIVE'
                            ? 'bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border-l-4 border-green-500'
                            : status === 'ALERT' || status === 'ALERT / vracanje / promjena'
                            ? 'bg-gradient-to-r from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 border-l-4 border-red-500'
                            : status === 'UGOVOR TURE'
                            ? 'bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 border-l-4 border-purple-500'
                            : status === 'RECOVERY'
                            ? 'bg-gradient-to-r from-orange-50 to-orange-100 hover:from-orange-100 hover:to-orange-200 border-l-4 border-orange-500'
                            : status === 'JARDA - ready for new drivers'
                            ? 'bg-gradient-to-r from-cyan-50 to-cyan-100 hover:from-cyan-100 hover:to-cyan-200 border-l-4 border-cyan-500'
                            : status === 'SHOP'
                            ? 'bg-gradient-to-r from-indigo-50 to-indigo-100 hover:from-indigo-100 hover:to-indigo-200 border-l-4 border-indigo-500'
                            : status === 'INACTIVE'
                            ? 'bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 border-l-4 border-gray-400'
                            : 'bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-100 hover:to-slate-200 border-l-4 border-slate-400'
                          }`}
                          onClick={() => toggleStatusCollapse(status)}
                        >
                          <div>
                            <h3 className="font-bold text-slate-900 text-base">
                              {getStatusConfig(status).label}
                            </h3>
                            <p className="text-xs text-slate-600">{statusTrailers.length} trailers</p>
                          </div>
                          <div className={`transform transition-transform duration-200 ${isCollapsed ? '' : 'rotate-180'}`}>
                            <ChevronDown className="w-5 h-5 text-slate-600" />
                          </div>
                        </div>
                      </td>
                    </tr>

                    {!isCollapsed && (
                      <>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th onClick={() => handleSort('unit_number')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Trailer {sortField === 'unit_number' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('driver')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Driver {sortField === 'driver' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('notes')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Note {sortField === 'notes' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('gps')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">GPS {sortField === 'gps' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('license_plate')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">License Plate {sortField === 'license_plate' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('inspection_expiration')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Inspection Exp {sortField === 'inspection_expiration' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('registration_expiration')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Reg Exp {sortField === 'registration_expiration' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('vin')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">VIN {sortField === 'vin' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('make')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Make {sortField === 'make' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('year')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Year {sortField === 'year' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th onClick={() => handleSort('owner')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Owner {sortField === 'owner' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                          <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                        </tr>
                        {statusTrailers.sort((a, b) => {
                          if (!sortField) return 0;
                          
                          let aVal: any = sortField === 'driver' 
                            ? (a.trucks?.drivers ? `${a.trucks.drivers.first_name} ${a.trucks.drivers.last_name}` : a.driver_assigned)
                            : (a as any)[sortField];
                          let bVal: any = sortField === 'driver'
                            ? (b.trucks?.drivers ? `${b.trucks.drivers.first_name} ${b.trucks.drivers.last_name}` : b.driver_assigned)
                            : (b as any)[sortField];
                          
                          if (aVal === null || aVal === undefined) aVal = '';
                          if (bVal === null || bVal === undefined) bVal = '';
                          
                          if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                          if (typeof bVal === 'string') bVal = bVal.toLowerCase();
                          
                          if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
                          if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
                          return 0;
                        }).map((trailer) => (
                      <tr 
                        key={trailer.id}
                        className="border-b border-slate-200 hover:bg-blue-50 transition-colors group"
                        style={{height: '36px'}}
                      >
                        <td className="px-3 py-2">
                          <button
                            onClick={() => navigate(`/trailers/${trailer.id}`)}
                            className="font-medium text-blue-600 hover:text-blue-800 hover:underline text-sm transition-colors"
                          >
                            {trailer.unit_number}
                          </button>
                        </td>
                        <td className="px-3 py-2" style={{ maxWidth: '120px' }}>
                          <span className="text-slate-700 text-xs truncate block">
                            {trailer.trucks?.drivers 
                              ? `${trailer.trucks.drivers.first_name} ${trailer.trucks.drivers.last_name}`
                              : trailer.driver_assigned || '-'
                            }
                          </span>
                        </td>
                        <td 
                          className="px-3 py-1 cursor-pointer hover:bg-blue-100 transition-colors relative group"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTrailerForNote(trailer);
                            setNoteInput(trailer.notes || '');
                            setShowNoteModal(true);
                          }}
                          style={{ maxWidth: '200px' }}
                        >
                          <div className="text-slate-700 text-xs">
                            {trailer.notes
                              ? trailer.notes.length > 20
                                ? `${trailer.notes.substring(0, 20)}...`
                                : trailer.notes
                              : '-'
                            }
                          </div>
                          {trailer.notes && trailer.notes.length > 20 && (
                            <div className="absolute left-0 top-full mt-1 hidden group-hover:block z-50 pointer-events-none">
                              <div className="bg-slate-800 text-white text-xs rounded-lg shadow-2xl px-3 py-2 max-w-xs whitespace-pre-wrap">
                                {trailer.notes}
                                <div className="absolute -top-1 left-4 w-2 h-2 bg-slate-800 transform rotate-45"></div>
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-0 py-0">
                          {editingGPS === trailer.id ? (
                            <select
                              autoFocus
                              value={trailer.gps || ''}
                              onChange={(e) => {
                                handleGPSChange(trailer.id, e.target.value);
                              }}
                              onBlur={() => setEditingGPS(null)}
                              className="w-full h-full px-3 py-2 border border-slate-300 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">-</option>
                              <option value="YES">YES</option>
                              <option value="NO">NO</option>
                              <option value="NIJE NAS">NIJE NAS</option>
                            </select>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingGPS(trailer.id);
                              }}
                              className={`w-full h-full flex items-center justify-center px-3 py-2 text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${
                                trailer.gps === 'YES' 
                                  ? 'bg-green-100 text-green-700'
                                  : trailer.gps === 'NO'
                                  ? 'bg-red-100 text-red-700'
                                  : trailer.gps === 'NIJE NAS'
                                  ? 'bg-orange-100 text-orange-700'
                                  : 'bg-slate-100 text-slate-700'
                              }`}
                            >
                              {trailer.gps || '-'}
                            </button>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-slate-700 text-sm">{trailer.license_plate || '-'}</span>
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-slate-700 text-xs">{trailer.inspection_expiration || '-'}</span>
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-slate-700 text-xs">{trailer.registration_expiration || '-'}</span>
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-slate-700 text-xs font-mono">{trailer.vin}</span>
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-slate-700 text-sm">{trailer.make}</span>
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-slate-700 text-sm">{trailer.year}</span>
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-slate-700 text-sm">{trailer.owner || '-'}</span>
                        </td>
                        <td className="px-0 py-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setTrailerForStatusChange(trailer);
                              setShowStatusModal(true);
                            }}
                            className={`w-full h-full flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${getStatusConfig(trailer.status).color}`}
                          >
                            <Circle className="w-1.5 h-1.5 fill-current" />
                            {getStatusConfig(trailer.status).label}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100">
                            <button
                              onClick={() => {
                                setSelectedTrailer(trailer);
                                setShowEditModal(true);
                              }}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            >
                              <Edit className="w-3 h-3" />
                              Edit
                            </button>
                            <button
                              onClick={() => {
                                setTrailerToDelete(trailer);
                                setShowDeleteConfirm(true);
                              }}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors"
                            >
                              <Trash2 className="w-3 h-3" />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                        ))}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
              
              {filteredTrailers.length === 0 && (
                <tr>
                  <td colSpan={12} className="px-6 py-12 text-center text-slate-500">
                    No trailers found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showCreateModal && (
        <CreateTrailerModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadTrailers();
          }}
        />
      )}

      {showEditModal && selectedTrailer && (
        <CreateTrailerModal
          trailer={selectedTrailer}
          onClose={() => {
            setShowEditModal(false);
            setSelectedTrailer(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedTrailer(null);
            loadTrailers();
          }}
        />
      )}

      {/* Note Modal */}
      {showNoteModal && selectedTrailerForNote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <div>
                <h2 className="text-2xl font-bold">Edit Note</h2>
                <p className="text-sm text-blue-100">Add or edit note for trailer {selectedTrailerForNote.unit_number}</p>
              </div>
              <button
                onClick={() => {
                  setShowNoteModal(false);
                  setSelectedTrailerForNote(null);
                  setNoteInput('');
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Note
              </label>
              <textarea
                value={noteInput}
                onChange={(e) => setNoteInput(e.target.value)}
                placeholder="Enter note for this trailer..."
                rows={4}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>

            <div className="p-6 border-t border-slate-200 bg-slate-50 flex gap-3">
              <button
                onClick={() => {
                  setShowNoteModal(false);
                  setSelectedTrailerForNote(null);
                  setNoteInput('');
                }}
                className="flex-1 px-6 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-white transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNote}
                disabled={savingNote}
                className="flex-1 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingNote ? 'Saving...' : 'Save Note'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Trailer Confirmation */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Trailer"
        message={trailerToDelete
          ? `Are you sure you want to delete trailer ${trailerToDelete.unit_number}? This action cannot be undone.`
          : 'Are you sure you want to delete this trailer?'}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteTrailer}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setTrailerToDelete(null);
        }}
        type="danger"
      />

      {/* Change Status Modal */}
      {showStatusModal && trailerForStatusChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">
                Change Trailer Status
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Trailer: {trailerForStatusChange.unit_number}
              </p>
            </div>
            <div className="p-6 space-y-3">
              {allStatuses.map((status) => (
                <button
                  key={status}
                  onClick={() => handleStatusChange(status)}
                  className={`w-full px-4 py-3 rounded-lg text-left font-medium transition-colors ${
                    trailerForStatusChange.status === status
                      ? 'bg-blue-100 text-blue-700 border-2 border-blue-500'
                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100 border-2 border-transparent'
                  }`}
                >
                  {getStatusConfig(status).label}
                </button>
              ))}
            </div>
            <div className="p-6 border-t border-slate-200 flex gap-3">
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setTrailerForStatusChange(null);
                }}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function CreateTrailerModal({ 
  trailer, 
  onClose, 
  onSuccess 
}: { 
  trailer?: Trailer;
  onClose: () => void; 
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState({
    unit_number: trailer?.unit_number || '',
    vin: trailer?.vin || '',
    year: trailer?.year || new Date().getFullYear(),
    make: trailer?.make || '',
    model: trailer?.model || '',
    trailer_type: trailer?.trailer_type || 'dry_van',
    length_feet: trailer?.length_feet || 53,
    width_feet: trailer?.width_feet || null,
    height_feet: trailer?.height_feet || null,
    weight_lbs: trailer?.weight_lbs || null,
    license_plate: trailer?.license_plate || '',
    license_state: trailer?.license_state || '',
    registration_expiration: trailer?.registration_expiration || '',
    inspection_expiration: trailer?.inspection_expiration || '',
    capacity_weight: trailer?.capacity_weight || 45000,
    tire_size: trailer?.tire_size || '',
    serial_number: trailer?.serial_number || '',
    tag_number: trailer?.tag_number || '',
    tag_state: trailer?.tag_state || '',
    dot_issued_date: trailer?.dot_issued_date || '',
    dot_state: trailer?.dot_state || '',
    dot_expiration_date: trailer?.dot_expiration_date || '',
    insurance_type: trailer?.insurance_type || '',
    insurance_carrier: trailer?.insurance_carrier || '',
    insurance_expiration_date: trailer?.insurance_expiration_date || '',
    notes: trailer?.notes || '',
    driver_assigned: trailer?.driver_assigned || '',
    gps: trailer?.gps || '',
    owner: trailer?.owner || '',
    truck_id: trailer?.truck_id || null,
    status: trailer?.status || 'ACTIVE',
  });
  const [trucks, setTrucks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [registrationFile, setRegistrationFile] = useState<File | null>(null);
  const [dotFile, setDotFile] = useState<File | null>(null);
  const isEditing = !!trailer;

  useEffect(() => {
    async function loadTrucks() {
      const { data } = await supabase
        .from('trucks')
        .select(`
          id,
          unit_number,
          drivers:driver_id (
            first_name,
            last_name
          )
        `)
        .order('unit_number');
      setTrucks(data || []);
    }
    loadTrucks();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);

    try {
      let registrationFileUrl = '';
      let dotFileUrl = '';

      // Upload files if selected
      if (registrationFile || dotFile) {
        // Upload registration file
        if (registrationFile) {
          const fileExt = registrationFile.name.split('.').pop();
          const fileName = `registration_${formData.unit_number}_${Date.now()}.${fileExt}`;
          const filePath = `trailer-documents/${formData.unit_number}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('company-documents')
            .upload(filePath, registrationFile, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
            .from('company-documents')
            .getPublicUrl(filePath);

          registrationFileUrl = publicUrl;
        }

        // Upload DOT file
        if (dotFile) {
          const fileExt = dotFile.name.split('.').pop();
          const fileName = `dot_${formData.unit_number}_${Date.now()}.${fileExt}`;
          const filePath = `trailer-documents/${formData.unit_number}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('company-documents')
            .upload(filePath, dotFile, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
            .from('company-documents')
            .getPublicUrl(filePath);

          dotFileUrl = publicUrl;
        }
      }

      const dataToSave = {
        ...formData,
        // Convert empty date strings to null
        inspection_expiration: formData.inspection_expiration || null,
        registration_expiration: formData.registration_expiration || null,
        dot_issued_date: formData.dot_issued_date || null,
        dot_expiration_date: formData.dot_expiration_date || null,
        insurance_expiration_date: formData.insurance_expiration_date || null,
        ...(registrationFileUrl && { registration_file_url: registrationFileUrl }),
        ...(dotFileUrl && { dot_file_url: dotFileUrl }),
      };

      if (isEditing) {
        const { error } = await supabase
          .from('trailers')
          .update(dataToSave)
          .eq('id', trailer.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase.from('trailers').insert(dataToSave);
        
        if (error) throw error;
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving trailer:', error);
      alert('Error saving trailer');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-slate-900">
            {isEditing ? 'Edit Trailer' : 'Add New Trailer'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Unit Number *
              </label>
              <input
                type="text"
                value={formData.unit_number}
                onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                GPS
              </label>
              <select
                value={formData.gps}
                onChange={(e) => setFormData({ ...formData, gps: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">-</option>
                <option value="YES">YES</option>
                <option value="NO">NO</option>
                <option value="NIJE NAS">NIJE NAS</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Assigned Truck
              </label>
              <select
                value={formData.truck_id || ''}
                onChange={(e) => setFormData({ ...formData, truck_id: e.target.value || null })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">No Truck Assigned</option>
                {trucks.map((truck) => (
                  <option key={truck.id} value={truck.id}>
                    {truck.unit_number}
                    {truck.drivers && ` - ${truck.drivers.first_name} ${truck.drivers.last_name}`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                VIN *
              </label>
              <input
                type="text"
                value={formData.vin}
                onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Year *
              </label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Make *
              </label>
              <input
                type="text"
                value={formData.make}
                onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Model *
              </label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Trailer Type *
              </label>
              <select
                value={formData.trailer_type}
                onChange={(e) => setFormData({ ...formData, trailer_type: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="dry_van">Dry Van</option>
                <option value="reefer">Reefer</option>
                <option value="flatbed">Flatbed</option>
                <option value="tanker">Tanker</option>
                <option value="lowboy">Lowboy</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Length (feet)
              </label>
              <input
                type="number"
                value={formData.length_feet}
                onChange={(e) => setFormData({ ...formData, length_feet: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Capacity (lbs)
              </label>
              <input
                type="number"
                value={formData.capacity_weight}
                onChange={(e) => setFormData({ ...formData, capacity_weight: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                License Plate
              </label>
              <input
                type="text"
                value={formData.license_plate}
                onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Inspection Expiration
              </label>
              <input
                type="date"
                value={formData.inspection_expiration}
                onChange={(e) => setFormData({ ...formData, inspection_expiration: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                License State
              </label>
              <input
                type="text"
                value={formData.license_state}
                onChange={(e) => setFormData({ ...formData, license_state: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Registration Expiration
              </label>
              <input
                type="date"
                value={formData.registration_expiration}
                onChange={(e) => setFormData({ ...formData, registration_expiration: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Owner
              </label>
              <input
                type="text"
                value={formData.owner}
                onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Width (ft)
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.width_feet || ''}
                onChange={(e) => setFormData({ ...formData, width_feet: e.target.value ? parseFloat(e.target.value) : null })}
                placeholder="e.g., 8.5"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Height (ft)
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.height_feet || ''}
                onChange={(e) => setFormData({ ...formData, height_feet: e.target.value ? parseFloat(e.target.value) : null })}
                placeholder="e.g., 13.5"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Weight (lbs)
              </label>
              <input
                type="number"
                value={formData.weight_lbs || ''}
                onChange={(e) => setFormData({ ...formData, weight_lbs: e.target.value ? parseInt(e.target.value) : null })}
                placeholder="e.g., 12000"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Tire Size
              </label>
              <input
                type="text"
                value={formData.tire_size}
                onChange={(e) => setFormData({ ...formData, tire_size: e.target.value })}
                placeholder="e.g., 295/75R22.5"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Serial Number
              </label>
              <input
                type="text"
                value={formData.serial_number}
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Tag Number
              </label>
              <input
                type="text"
                value={formData.tag_number}
                onChange={(e) => setFormData({ ...formData, tag_number: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Tag State
              </label>
              <input
                type="text"
                value={formData.tag_state}
                onChange={(e) => setFormData({ ...formData, tag_state: e.target.value })}
                maxLength={2}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                DOT Issued Date
              </label>
              <input
                type="date"
                value={formData.dot_issued_date}
                onChange={(e) => setFormData({ ...formData, dot_issued_date: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                DOT State
              </label>
              <input
                type="text"
                value={formData.dot_state}
                onChange={(e) => setFormData({ ...formData, dot_state: e.target.value })}
                maxLength={2}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                DOT Expiration Date
              </label>
              <input
                type="date"
                value={formData.dot_expiration_date}
                onChange={(e) => setFormData({ ...formData, dot_expiration_date: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Insurance Type
              </label>
              <select
                value={formData.insurance_type}
                onChange={(e) => setFormData({ ...formData, insurance_type: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Type</option>
                <option value="liability">Liability</option>
                <option value="comprehensive">Comprehensive</option>
                <option value="collision">Collision</option>
                <option value="full">Full Coverage</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Insurance Carrier
              </label>
              <input
                type="text"
                value={formData.insurance_carrier}
                onChange={(e) => setFormData({ ...formData, insurance_carrier: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Insurance Expiration Date
              </label>
              <input
                type="date"
                value={formData.insurance_expiration_date}
                onChange={(e) => setFormData({ ...formData, insurance_expiration_date: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Status *
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="ACTIVE">Active</option>
                <option value="ALERT / vracanje / promjena">Alert / vracanje / promjena</option>
                <option value="UGOVOR TURE">Ugovor Ture</option>
                <option value="RECOVERY">Recovery</option>
                <option value="JARDA - ready for new drivers">Jarda - ready for new drivers</option>
                <option value="SHOP">Shop</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Document Uploads */}
            <div className="col-span-2 border-t border-slate-200 pt-4">
              <h3 className="text-md font-semibold text-slate-900 mb-3">Document Uploads</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Registration File</label>
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setRegistrationFile(file);
                        }
                      }}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                    {registrationFile && (
                      <div className="text-sm text-slate-600">
                        Selected: {registrationFile.name}
                      </div>
                    )}
                    <p className="text-xs text-slate-500">Accepts PDF and images (JPG, PNG)</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">DOT File</label>
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setDotFile(file);
                        }
                      }}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                    {dotFile && (
                      <div className="text-sm text-slate-600">
                        Selected: {dotFile.name}
                      </div>
                    )}
                    <p className="text-xs text-slate-500">Accepts PDF and images (JPG, PNG)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50"
            >
              {loading ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Trailer' : 'Create Trailer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
